/**
 * /market/:slug/coleccion/:coleccion — Landing comercial de colección (Ola 1.2).
 *
 * Una "colección" es un tag de producto, pero el comercio puede definirle un
 * slug propio en el Builder (`resolveCollectionTarget` primero busca por ahí);
 * si no hay landing armada, cae al tag de producto que matchee el slug.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { Tag } from "lucide-react";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketLandingPage } from "@/components/market/MarketLandingPage";
import { MarketPlpSkeleton } from "@/components/market/MarketSkeletons";
import {
  getPublishedPlp,
  landingPath,
  landingSeoHead,
  resolveCollectionTarget,
  resolveLandingConfig,
} from "@/lib/market-landing";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/coleccion/$coleccion")({
  pendingComponent: MarketPlpSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: CollectionLandingRoute,
  head: ({ params, loaderData }) => {
    const manifestLink = { rel: "manifest", href: `/api/manifest/${params.slug}` };
    const result = loaderData as MarketCatalogResult | undefined;
    if (!result?.ok) {
      return { meta: [{ title: `${params.coleccion} — ${params.slug}` }], links: [manifestLink, FONT_LINK] };
    }

    const data = result.data;
    const plp = getPublishedPlp(data);
    const target = resolveCollectionTarget(data.products, plp, params.coleccion);
    if (!target) {
      return {
        meta: [{ title: `Colección — ${data.tenant.name}` }],
        links: [manifestLink, FONT_LINK],
      };
    }

    const landing = plp ? resolveLandingConfig(plp, "collection", target.tag) : null;
    const seo = landing
      ? landingSeoHead({
          storeName: data.tenant.name,
          landing,
          fallbackTitle: target.tag,
          canonicalPath: landingPath(params.slug, "collection", target.slug),
        })
      : null;

    return {
      meta: seo?.meta ?? [
        { title: `${target.tag} — ${data.tenant.name}` },
        { name: "description", content: `Descubrí ${target.tag} en ${data.tenant.name}` },
      ],
      links: [...(seo?.links ?? []), manifestLink, FONT_LINK],
      scripts: seo?.jsonLd
        ? [
            {
              tag: "script" as const,
              attrs: { type: "application/ld+json" },
              children: JSON.stringify(seo.jsonLd),
            },
          ]
        : undefined,
    };
  },
});

function CollectionLandingRoute() {
  const { slug, coleccion } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!result.ok) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <Tag className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
        <p className="text-muted-foreground">{result.error}</p>
      </div>
    );
  }

  const data = result.data;
  const plp = getPublishedPlp(data);
  const target = resolveCollectionTarget(data.products, plp, coleccion);

  if (!target) {
    return (
      <div className="market-surface min-h-screen" style={data.brand.surfaceStyle}>
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
          <Tag className="h-12 w-12 text-[var(--market-on-surface-variant)]" />
          <h1 className="market-font-display text-xl font-bold text-[var(--market-on-surface)]">
            Colección no encontrada
          </h1>
          <Link
            to="/market/$slug"
            params={{ slug }}
            className="rounded-full bg-[var(--market-accent)] px-5 py-2.5 text-sm font-semibold text-[var(--primary-foreground)]"
          >
            Volver a la tienda
          </Link>
        </div>
      </div>
    );
  }

  return (
    <MarketLandingPage
      slug={slug}
      data={data}
      kind="collection"
      param={target.slug}
      collectionTag={target.tag}
      title={target.tag}
    />
  );
}
